import axiosApiClient from "..";
import { handleError } from "../../utils/errors";

const BASE_URL = "organization";

class OrganizationService {
  fetchInfo = async () => {
    try {
      const response = await axiosApiClient.get(`${BASE_URL}/me`);
      if (response.status !== 200) {
        throw new Error();
      }
      const { organizationUser, allUserOrganizations } = response.data.data;

      return { organizationUser, allUserOrganizations };
    } catch (err) {
      throw handleError(err, "Error occurred while fetching user's info.");
    }
  };

  setOrganizationDescription = async (body) => {
    try {
      const response = await axiosApiClient.post(
        `${BASE_URL}/description`,
        body
      );
      if (response.status !== 200) {
        throw new Error();
      }
      const data = response.data;
      return data;
    } catch (err) {
      throw handleError(
        err,
        "Error occurred while setting organization description."
      );
    }
  };

  setOrganizationLogo = async (body) => {
    try {
      const response = await axiosApiClient.post(`${BASE_URL}/logo`, body);
      if (response.status !== 201) {
        throw new Error();
      }

      const data = response.data.data;

      return { data };
    } catch (err) {
      throw handleError(err, "Error occurred while setting organization logo.");
    }
  };

  setActiveUserOrganization = async (organizationId) => {
    try {
      const response = await axiosApiClient.post(
        `${BASE_URL}/active-organization`,
        { organizationId }
      );
      if (response.status !== 200) {
        throw new Error();
      }
      const data = response.data.data;
      return data;
    } catch (err) {
      throw handleError(
        err,
        "Error occurred while setting active organization."
      );
    }
  };

  getOrganizationMembers = async (page) => {
    try {
      const response = await axiosApiClient.get(
        `${BASE_URL}/members?page=${page}`
      );
      if (response.status !== 200) {
        throw new Error();
      }
      const { data, message, maxReached, total } = response.data;

      return { data, message, maxReached, total, page };
    } catch (err) {
      throw handleError(
        err,
        "Error occurred while fetching organization members."
      );
    }
  };

  searchUsers = async (page, search) => {
    try {
      let formattedSearch = search;
      if (!search) {
        search = "";
      }

      if (formattedSearch) {
        formattedSearch = encodeURIComponent(search);
      }

      const response = await axiosApiClient.get(
        `${BASE_URL}/search-users/?page=${page}&search=${formattedSearch}`
      );
      if (response.status !== 200) {
        throw new Error();
      }
      const { data, message, maxReached, total } = response.data;
      return { data, message, maxReached, total, page };
    } catch (err) {
      throw handleError(err, "Error occurred while searching users");
    }
  };

  addMemberToOrganization = async (body) => {
    try {
      const response = await axiosApiClient.post(
        `${BASE_URL}/add-member`,
        body
      );
      if (response.status !== 200) {
        throw new Error();
      }
      const { data } = response.data;
      return { data };
    } catch (err) {
      throw handleError(err, "Error occurred while adding user to members");
    }
  };

  removeMember = async (memberId) => {
    try {
      const response = await axiosApiClient.post(`${BASE_URL}/remove-member`, {
        memberId,
      });
      if (response.status !== 200) {
        throw new Error();
      }
      const { data } = response.data;
      return { data };
    } catch (err) {
      throw handleError(err, "Error occurred while removing user from members");
    }
  };

  getUserMemberWishlistItems = async (id) => {
    try {
      const response = await axiosApiClient.get(`${BASE_URL}/wishlist/${id}`);
      if (response.status !== 200) {
        throw new Error();
      }
      const { data } = response.data;
      return { data };
    } catch (err) {
      throw handleError(
        err,
        "Error occurred while fetching user wishlist items"
      );
    }
  };

  removeItemFromWishlist = async (itemId) => {
    try {
      const response = await axiosApiClient.delete(
        `${BASE_URL}/wishlist/${itemId}`
      );
      if (response.status !== 200) {
        throw new Error();
      }
      const data = response.data;
      return { itemId };
    } catch (err) {
      throw handleError(
        err,
        "Error occurred while removing item from wishlist"
      );
    }
  };

  updateItem = async (formData) => {
    try {
      const response = await axiosApiClient.put(`${BASE_URL}/item`, formData);
      if (response.status !== 200) {
        throw new Error();
      }
      const { data } = response.data;
      return { data };
    } catch (err) {
      throw handleError(err, "Error occurred while updating item");
    }
  };

  addNewOrganization = async (organizationName) => {
    try {
      const response = await axiosApiClient.post(
        `${BASE_URL}/add-organization`,
        { organizationName }
      );
      if (response.status !== 200) {
        throw new Error();
      }
      const { data } = response.data;
      return { data };
    } catch (err) {
      throw handleError(err, "Error occurred while creating new organization");
    }
  };

  removeOrganization = async (organizationId) => {
    try {
      const response = await axiosApiClient.post(
        `${BASE_URL}/remove-organization`,
        { organizationId }
      );
      if (response.status !== 200) {
        throw new Error();
      }
      const { data } = response.data;
      return { data };
    } catch (err) {
      throw handleError(err, "Error occurred while removing organization");
    }
  };

  setPriorityToOrganizationMembers = async (members) => {
    try {
      const response = await axiosApiClient.post(
        `${BASE_URL}/priority-member`,
        { members }
      );
      if (response.status !== 200) {
        throw new Error();
      }
      const { data } = response.data;
      return { data };
    } catch (err) {
      throw handleError(
        err,
        "Error occurred while setting priority organization to members"
      );
    }
  };

  setPriorityToOrganizations = async (organizations) => {
    try {
      const response = await axiosApiClient.post(
        `${BASE_URL}/priority-organizations`,
        { organizations }
      );
      if (response.status !== 200) {
        throw new Error();
      }
      const { data } = response.data;
      return { data };
    } catch (err) {
      throw handleError(
        err,
        "Error occurred while setting priority to organizations"
      );
    }
  };

  setOrganizationMemberDescription = async (body) => {
    try {
      const response = await axiosApiClient.post(
        `${BASE_URL}/member-description/${body.id}`,
        body
      );
      if (response.status !== 200) {
        throw new Error();
      }
      const data = response.data;
      return data;
    } catch (err) {
      throw handleError(
        err,
        "Error occurred while setting organization member description."
      );
    }
  };
}
const organizationService = new OrganizationService();

export default organizationService;
